import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Form, ButtonGroup, Button } from 'react-bootstrap';
import { omit as _omit } from 'lodash';

import { addNotification } from 'actions/NotificationsActions';
import { formStatusDispatchHelper } from 'actions/FormStatusActions';
import { getThumbFile } from 'helpers/items';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Data from 'components/Data';
import Entity from 'components/Entity';

import FormInput from 'components/forms/FormInput';
import FormTags from 'components/forms/FormTags';

import Loader from 'components/layout/Loader';

import { entityMetaMapping } from 'js/constants';

class CmsMediaSelectorFileData extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      entityLoaded: false,
      entityChanged: false,
      setAfterSave: false,
    };

    this.entity = new Entity();
    this.entity.setData({});

    if (props.meta[entityMetaMapping.cmsFile] !== undefined && !props.meta[entityMetaMapping.cmsFile].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.cmsFile);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      cmsFile,
      formStatus,
      doNotify,
      selectFile,
    } = this.props;

    if (prevProps.cmsFile.pending && !cmsFile.pending) {
      this.entity.setData(cmsFile.data);
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (
      meta[entityMetaMapping.cmsFile] !== undefined &&
      !meta[entityMetaMapping.cmsFile].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.cmsFile);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      doNotify(1, messages.image_data_saved);
      if (this.state.setAfterSave) {
        this.setState({ setAfterSave: false }, () => {
          selectFile(formStatus.response);
        });
      }
    }
  };

  saveAndSet = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // if we are editing a page, we have to make sure the parent form will not be submitted
    }

    this.setState(
      prevState => ({ entity: prevState.entity.validate(), setAfterSave: true }),
      () => {
        const { id, onSubmit } = this.props;
        const { entity } = this.state;

        if (entity.isValid) {
          const data = _omit(entity.processedData, ['variations', 'links', 'type', 'webLink']);
          onSubmit(id, data);
        }
      }
    );
  };

  onSubmit = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // if we are editing a page, we have to make sure the parent form will not be submitted
    }

    this.setState(
      prevState => ({ entity: prevState.entity.validate(), setAfterSave: false }),
      () => {
        const { id, onSubmit } = this.props;
        const { entity } = this.state;

        if (entity.isValid) {
          const data = _omit(entity.processedData, ['variations', 'links', 'type', 'webLink', 'videoInfo']);
          onSubmit(id, data);
        }
      }
    );
  };

  render() {
    const {
      intl: { messages },
      id,
      mediaType,
      cmsFile,
      formStatus,
    } = this.props;
    const { entityLoaded, entityChanged, entity } = this.state;

    let background = {};
    let isVideoUploaded = false;
    let videoThumbUrl = null;

    switch (mediaType) {
      case 'video': {
        if (!cmsFile.pending && cmsFile.data.video_info) {
          isVideoUploaded =
            cmsFile.data.video_info.thumb_transcoding_finished && cmsFile.data.video_info.web_transcoding_finished;
          videoThumbUrl = cmsFile.data.video_info.thumb_link;
        }
        break;
      }

      default: {
        const thumbFile = getThumbFile(cmsFile.data);
        if (thumbFile) {
          background = {
            ...background,
            backgroundImage: 'url(' + thumbFile.web_link + ')',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          };
        }
      }
    }

    return (
      <div>
        <Data dataName="cmsFile" url={`/api/cmsMedia/files/${id}.json`} data={cmsFile} />

        {cmsFile.pending || !entityLoaded ? (
          <Loader />
        ) : (
          <Form onSubmit={this.onSubmit}>
            <h4 class="overflow-ellipsis m-t-0" title={entity.getTitle() ? entity.getTitle() : entity.getFileName()}>
              {entity.getTitle() ? entity.getTitle() : entity.getFileName()}
            </h4>

            {mediaType === 'image' && (
              <div
                style={{
                  height: '100px',
                  ...background,
                }}
              />
            )}

            {mediaType === 'video' && isVideoUploaded && (
              <div className="video-preview">
                <video controls>
                  <source src={videoThumbUrl} type="video/mp4" />
                </video>
              </div>
            )}

            <div class="m-t-10 form-group-attached">
              <FormInput
                label={messages.file_name}
                name="fileName"
                value={entity.getFileName()}
                meta={entity.getFileNameMetaInformation()}
                validation={entity.getFileNameValidationResult()}
                onChange={this.updateEntity}
                disabled={formStatus.pending}
              />
              <FormInput
                label={messages.title}
                name="title"
                value={entity.getTitle()}
                meta={entity.getTitleMetaInformation()}
                validation={entity.getTitleValidationResult()}
                onChange={this.updateEntity}
                disabled={formStatus.pending}
              />
              <FormInput
                label={messages.description}
                name="description"
                value={entity.getDescription()}
                meta={entity.getDescriptionMetaInformation()}
                validation={entity.getDescriptionValidationResult()}
                onChange={this.updateEntity}
                disabled={formStatus.pending}
              />
              <FormTags
                label={messages.tags}
                name="tags"
                value={entity.getTags()}
                meta={entity.getTagsMetaInformation()}
                validation={entity.getTagsValidationResult()}
                onChange={this.updateEntity}
                messages={messages}
                disabled={formStatus.pending}
              />
            </div>

            <div class="m-t-10">
              {entityChanged ? (
                <ButtonGroup justified>
                  <Button disabled={formStatus.pending} href="#save" onClick={this.onSubmit}>
                    {messages.save}
                  </Button>
                  <Button disabled={formStatus.pending} href="#saveAndSet" onClick={this.saveAndSet}>
                    {messages.save_and_set_image}
                  </Button>
                </ButtonGroup>
              ) : (
                <Button block bsStyle="primary" onClick={() => this.props.selectFile(cmsFile.data)}>
                  {messages.set_image}
                </Button>
              )}
            </div>
          </Form>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    cmsFile: state.data.cmsFile,
    meta: state.entityMeta,
    formStatus: state.formStatus.cmsFile,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    onSubmit: (fileId, args) => {
      formStatusDispatchHelper('cmsFile', '/api/cmsMedia/files/' + fileId + '.json', args, 'put', dispatch);
    },
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CmsMediaSelectorFileData)
);
